import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const propertyTypes = [
  { id: 'residential', label: 'Residential', icon: 'ri-home-4-line', multiplier: 1 },
  { id: 'commercial', label: 'Commercial', icon: 'ri-building-line', multiplier: 1.4 },
  { id: 'industrial', label: 'Industrial', icon: 'ri-building-4-line', multiplier: 1.8 },
];

const serviceTypes = [
  { id: 'water', label: 'Water Damage', icon: 'ri-drop-line', base: 1200 },
  { id: 'flood', label: 'Flood Damage', icon: 'ri-flood-line', base: 2500 },
  { id: 'mold', label: 'Mold Remediation', icon: 'ri-virus-line', base: 1800 },
  { id: 'fire', label: 'Fire & Smoke', icon: 'ri-fire-line', base: 3000 },
  { id: 'sewage', label: 'Sewage Backup', icon: 'ri-water-flash-line', base: 2000 },
  { id: 'storm', label: 'Storm Damage', icon: 'ri-thunderstorms-line', base: 2200 },
];

const severityLevels = [
  { id: 'minor', label: 'Minor', desc: 'Surface-level, caught early', multiplier: 1, color: 'text-green-600' },
  { id: 'moderate', label: 'Moderate', desc: 'Some structural involvement', multiplier: 1.6, color: 'text-amber-600' },
  { id: 'severe', label: 'Severe', desc: 'Extensive structural damage', multiplier: 2.4, color: 'text-red-600' },
];

const addOns = [
  { id: 'mold_test', label: 'Mold Testing & Inspection', icon: 'ri-microscope-line', cost: 350 },
  { id: 'contents', label: 'Contents Cleaning & Pack-Out', icon: 'ri-box-3-line', cost: 600 },
  { id: 'odor', label: 'Odor Removal Treatment', icon: 'ri-wind-line', cost: 280 },
  { id: 'insurance', label: 'Insurance Claim Assistance', icon: 'ri-shield-check-line', cost: 0, note: 'Free' },
  { id: 'emergency', label: '24/7 Emergency Response', icon: 'ri-alarm-warning-line', cost: 250 },
];

function getAreaMultiplier(sqft: number): number {
  if (sqft <= 100) return 0.6;
  if (sqft <= 200) return 1;
  if (sqft <= 400) return 1.6;
  if (sqft <= 600) return 2.2;
  if (sqft <= 800) return 2.8;
  return 3.5 + (sqft - 800) / 400;
}

function getAreaLabel(sqft: number): string {
  if (sqft >= 1000) return '1,000+ sq ft';
  return `${sqft} sq ft`;
}

function getEstimate(
  propertyType: string,
  serviceType: string,
  areaSqft: number,
  severity: string,
  selectedAddOns: string[]
) {
  const base = serviceTypes.find(d => d.id === serviceType)?.base ?? 0;
  const propMult = propertyTypes.find(p => p.id === propertyType)?.multiplier ?? 1;
  const areaMult = getAreaMultiplier(areaSqft);
  const sevMult = severityLevels.find(s => s.id === severity)?.multiplier ?? 1;
  const addOnTotal = addOns
    .filter(a => selectedAddOns.includes(a.id))
    .reduce((sum, a) => sum + a.cost, 0);
  const subtotal = base * propMult * areaMult * sevMult + addOnTotal;
  return {
    low: Math.round(subtotal * 0.85),
    high: Math.round(subtotal * 1.15),
    base: Math.round(base * propMult * areaMult * sevMult),
    addOnTotal,
  };
}

export default function CostCalculatorPage() {
  const [propertyType, setPropertyType] = useState('');
  const [areaSqft, setAreaSqft] = useState(200);
  const [serviceType, setServiceType] = useState('');
  const [severity, setSeverity] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Restoration Cost Calculator",
      "description": "Free online cost calculator for water damage restoration, fire damage cleanup, mold remediation, and storm damage repair services in Merced, CA.",
      "url": `${import.meta.env.VITE_SITE_URL}/calculator`,
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "url": import.meta.env.VITE_SITE_URL
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const toggleAddOn = (id: string) => {
    setSelectedAddOns(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const reset = () => {
    setPropertyType('');
    setAreaSqft(200);
    setServiceType('');
    setSeverity('');
    setSelectedAddOns([]);
  };

  const stepsComplete = [propertyType, areaSqft > 0, serviceType, severity].filter(Boolean).length;
  const isReady = !!propertyType && !!serviceType && !!severity;
  const estimate = isReady ? getEstimate(propertyType, serviceType, areaSqft, severity, selectedAddOns) : null;

  const selectedProperty = propertyTypes.find(p => p.id === propertyType);
  const selectedService = serviceTypes.find(d => d.id === serviceType);
  const selectedSeverity = severityLevels.find(s => s.id === severity);
  const selectedAddOnItems = addOns.filter(a => selectedAddOns.includes(a.id));

  const sliderPercent = Math.min((areaSqft / 1000) * 100, 100);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formEmail.trim() || !formPhone.trim()) return;
    setFormSubmitting(true);
    setFormError('');

    const addOnLabels = selectedAddOnItems.map(a => a.label).join(', ') || 'None';
    const estimateText = estimate
      ? `$${estimate.low.toLocaleString()} – $${estimate.high.toLocaleString()}`
      : 'Not calculated';

    const body = new URLSearchParams({
      name: formName,
      email: formEmail,
      phone: formPhone,
      message: formMessage,
      property_type: selectedProperty?.label ?? 'Not selected',
      area_size: getAreaLabel(areaSqft),
      service_type: selectedService?.label ?? 'Not selected',
      severity: selectedSeverity?.label ?? 'Not selected',
      add_ons: addOnLabels,
      estimated_range: estimateText,
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6qh15lsbgrc47fim6e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormSuccess(true);
        setFormName('');
        setFormEmail('');
        setFormPhone('');
        setFormMessage('');
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } catch {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-teal-700 to-teal-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-20 w-64 h-64 rounded-full bg-white/20 blur-3xl"></div>
          <div className="absolute bottom-0 right-20 w-80 h-80 rounded-full bg-teal-300/20 blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-teal-500/30 text-teal-100 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 uppercase tracking-widest">
            Free Tool
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">Restoration Cost Calculator</h1>
          <p className="text-teal-100 text-lg max-w-2xl mx-auto">
            Make your selections and see your live estimate update instantly on the right.
          </p>
        </div>
      </section>

      {/* Widget Layout */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 items-start">

            {/* LEFT — Selections */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Step 1 — Property Type */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Property Type</h2>
                    <p className="text-gray-400 text-xs">Select the type of property affected</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {propertyTypes.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setPropertyType(p.id)}
                      className={`flex flex-col items-center gap-3 p-5 rounded-xl border-2 transition-all cursor-pointer text-center ${
                        propertyType === p.id
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 bg-teal-50">
                        <i className={`${p.icon} text-2xl ${propertyType === p.id ? 'text-teal-600' : 'text-gray-400'}`}></i>
                      </div>
                      <span className="text-sm font-semibold leading-tight">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2 — Affected Area Size (Slider) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Affected Area Size</h2>
                    <p className="text-gray-400 text-xs">Drag the slider to estimate the square footage of damage</p>
                  </div>
                </div>

                {/* Value Display */}
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <span className="text-4xl font-bold text-teal-700">
                      {areaSqft >= 1000 ? '1,000+' : areaSqft.toLocaleString()}
                    </span>
                    <span className="text-lg text-gray-500 ml-2">sq ft</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      areaSqft < 200 ? 'bg-green-100 text-green-700' :
                      areaSqft < 500 ? 'bg-amber-100 text-amber-700' :
                      areaSqft < 800 ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {areaSqft < 200 ? 'Small Area' : areaSqft < 500 ? 'Medium Area' : areaSqft < 800 ? 'Large Area' : 'Extra Large'}
                    </span>
                  </div>
                </div>

                {/* Slider */}
                <div className="relative mb-3">
                  <div className="relative h-2 bg-gray-200 rounded-full">
                    <div
                      className="absolute left-0 top-0 h-2 bg-gradient-to-r from-teal-400 to-teal-600 rounded-full transition-all"
                      style={{ width: `${sliderPercent}%` }}
                    ></div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={1000}
                    step={10}
                    value={areaSqft}
                    onChange={e => setAreaSqft(Number(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer h-2"
                    style={{ margin: 0 }}
                  />
                  {/* Thumb */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white border-2 border-teal-600 rounded-full shadow-md transition-all pointer-events-none"
                    style={{ left: `calc(${sliderPercent}% - 10px)` }}
                  ></div>
                </div>

                {/* Scale Labels */}
                <div className="flex justify-between text-xs text-gray-400 mt-4">
                  <span>0 sq ft</span>
                  <span>250</span>
                  <span>500</span>
                  <span>750</span>
                  <span>1,000+</span>
                </div>

                {/* Quick Select Buttons */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {[100, 250, 500, 1000].map(val => (
                    <button
                      key={val}
                      onClick={() => setAreaSqft(val)}
                      className={`py-2 rounded-lg text-xs font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                        areaSqft === val
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-gray-200 text-gray-500 hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      {val === 1000 ? '1,000+' : val} sq ft
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3 — Service Type */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Select Service Type</h2>
                    <p className="text-gray-400 text-xs">Choose the service you need</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {serviceTypes.map(d => (
                    <button
                      key={d.id}
                      onClick={() => setServiceType(d.id)}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        serviceType === d.id
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 bg-teal-50">
                        <i className={`${d.icon} text-xl ${serviceType === d.id ? 'text-teal-600' : 'text-gray-400'}`}></i>
                      </div>
                      <span className="text-sm font-semibold leading-tight">{d.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 4 — Severity */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Damage Severity</h2>
                    <p className="text-gray-400 text-xs">How severe is the damage?</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {severityLevels.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSeverity(s.id)}
                      className={`flex flex-col gap-1 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        severity === s.id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <span className={`text-sm font-bold ${severity === s.id ? 'text-teal-700' : s.color}`}>{s.label}</span>
                      <span className="text-xs text-gray-400 leading-tight">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 5 — Add-ons */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-7 h-7 bg-gray-300 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">5</span>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Additional Services <span className="text-gray-400 font-normal text-sm">(Optional)</span></h2>
                    <p className="text-gray-400 text-xs">Select any extra services you may need</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {addOns.map(a => (
                    <button
                      key={a.id}
                      onClick={() => toggleAddOn(a.id)}
                      className={`w-full flex items-center justify-between gap-3 p-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                        selectedAddOns.includes(a.id)
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/40'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selectedAddOns.includes(a.id) ? 'bg-teal-600 border-teal-600' : 'border-gray-300'}`}>
                          {selectedAddOns.includes(a.id) && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${a.icon} text-base ${selectedAddOns.includes(a.id) ? 'text-teal-600' : 'text-gray-400'}`}></i>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{a.label}</span>
                      </div>
                      <span className={`text-sm font-bold whitespace-nowrap ${a.cost === 0 ? 'text-teal-600' : 'text-gray-600'}`}>
                        {a.note ?? `+$${a.cost.toLocaleString()}`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — Live Estimate Panel */}
            <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-24">
              <div className={`rounded-2xl overflow-hidden shadow-xl border transition-all duration-300 ${isReady ? 'border-teal-200' : 'border-gray-200'}`}>

                {/* Panel Header */}
                <div className={`px-6 py-5 transition-all duration-500 ${isReady ? 'bg-gradient-to-br from-teal-600 to-teal-800' : 'bg-gradient-to-br from-gray-500 to-gray-700'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white/70 text-xs font-semibold uppercase tracking-widest">Live Estimate</p>
                    {isReady && (
                      <button onClick={reset} className="text-white/60 hover:text-white text-xs flex items-center gap-1 cursor-pointer transition-colors whitespace-nowrap">
                        <i className="ri-refresh-line text-sm"></i> Reset
                      </button>
                    )}
                  </div>

                  {isReady && estimate ? (
                    <div className="mt-2">
                      <div className="text-4xl font-bold text-white leading-none">
                        ${estimate.low.toLocaleString()}
                        <span className="text-2xl text-white/70"> – </span>
                        ${estimate.high.toLocaleString()}
                      </div>
                      <p className="text-white/60 text-xs mt-2">Estimated cost range for your project</p>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <div className="text-3xl font-bold text-white/40">$— – $—</div>
                      <p className="text-white/50 text-xs mt-2">
                        Complete all steps to see your estimate
                      </p>
                    </div>
                  )}
                </div>

                {/* Progress Indicator */}
                <div className="bg-white px-6 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4].map(step => {
                      const done = step === 1 ? !!propertyType : step === 2 ? areaSqft > 0 : step === 3 ? !!serviceType : !!severity;
                      return (
                        <div key={step} className="flex items-center gap-1.5 flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${done ? 'bg-teal-600' : 'bg-gray-200'}`}>
                            {done
                              ? <i className="ri-check-line text-white text-xs"></i>
                              : <span className="text-gray-400 text-xs font-bold">{step}</span>
                            }
                          </div>
                          {step < 4 && <div className={`flex-1 h-0.5 rounded-full transition-colors ${done ? 'bg-teal-400' : 'bg-gray-200'}`}></div>}
                        </div>
                      );
                    })}
                    <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">{stepsComplete}/4 done</span>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="bg-white px-6 py-5 space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${selectedProperty?.icon ?? 'ri-home-4-line'} text-sm ${selectedProperty ? 'text-teal-600' : 'text-gray-300'}`}></i>
                        </div>
                        <span className="text-sm text-gray-500">Property Type</span>
                      </div>
                      <span className={`text-sm font-semibold ${selectedProperty ? 'text-gray-800' : 'text-gray-300'}`}>
                        {selectedProperty?.label ?? '—'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-layout-line text-sm text-teal-600"></i>
                        </div>
                        <span className="text-sm text-gray-500">Area Size</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">
                        {getAreaLabel(areaSqft)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${selectedService?.icon ?? 'ri-drop-line'} text-sm ${selectedService ? 'text-teal-600' : 'text-gray-300'}`}></i>
                        </div>
                        <span className="text-sm text-gray-500">Service Type</span>
                      </div>
                      <span className={`text-sm font-semibold ${selectedService ? 'text-gray-800' : 'text-gray-300'}`}>
                        {selectedService?.label ?? '—'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`ri-bar-chart-line text-sm ${selectedSeverity ? 'text-teal-600' : 'text-gray-300'}`}></i>
                        </div>
                        <span className="text-sm text-gray-500">Severity</span>
                      </div>
                      <span className={`text-sm font-semibold ${selectedSeverity ? selectedSeverity.color : 'text-gray-300'}`}>
                        {selectedSeverity?.label ?? '—'}
                      </span>
                    </div>
                  </div>

                  {isReady && estimate && (
                    <div className="pt-1 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Base restoration cost</span>
                        <span className="font-semibold text-gray-700">${estimate.base.toLocaleString()}</span>
                      </div>

                      {selectedAddOnItems.length > 0 && (
                        <>
                          {selectedAddOnItems.map(a => (
                            <div key={a.id} className="flex justify-between text-sm">
                              <span className="text-gray-400 flex items-center gap-1">
                                <i className="ri-add-line text-xs"></i>{a.label}
                              </span>
                              <span className="font-medium text-gray-600">
                                {a.cost === 0 ? 'Free' : `+$${a.cost.toLocaleString()}`}
                              </span>
                            </div>
                          ))}
                        </>
                      )}

                      <div className="border-t border-gray-200 pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-sm font-bold text-gray-700">Estimated Range</span>
                          <span className="text-sm font-bold text-teal-700">
                            ${estimate.low.toLocaleString()} – ${estimate.high.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isReady && (
                    <div className="py-4 text-center">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-calculator-line text-3xl text-gray-200"></i>
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        Select property type, service, area size, and severity to see your live estimate.
                      </p>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="bg-gray-50 px-6 py-5 border-t border-gray-100 space-y-3">
                  <a
                    href="tel:2094512288"
                    className="flex items-center justify-center gap-2 w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl font-bold transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-phone-line"></i>
                    Call for Exact Quote
                  </a>
                  <button
                    onClick={() => { setShowContactModal(true); setFormSuccess(false); setFormError(''); }}
                    className="flex items-center justify-center gap-2 w-full bg-white hover:bg-gray-100 text-teal-700 border-2 border-teal-600 px-6 py-3 rounded-xl font-bold transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-mail-send-line"></i>
                    Contact Us
                  </button>
                  <p className="text-center text-gray-400 text-xs">
                    Free on-site assessment · No obligation
                  </p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-500 text-base"></i>
                </div>
                <p className="text-amber-800 text-xs leading-relaxed">
                  This is a general estimate only. Actual costs depend on a professional on-site assessment. Call <a href="tel:2094512288" className="font-semibold underline cursor-pointer">(209) 451-2288</a> for a free, accurate quote.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-800 px-6 py-5 flex items-start justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">Contact Us</h2>
                <p className="text-teal-100 text-xs mt-0.5">We'll reach out with a precise quote for your project</p>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/10 flex-shrink-0"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {formSuccess ? (
              <div className="px-6 py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-gray-900 font-bold text-lg mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Thank you! A confirmation has been sent to your email. Our team will contact you shortly with a detailed quote.
                </p>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Estimate Summary Strip */}
                {isReady && estimate && (
                  <div className="bg-teal-50 border-b border-teal-100 px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-teal-700">
                      <i className="ri-calculator-line text-sm"></i>
                      <span className="font-medium">Your Estimate:</span>
                      <span className="font-bold">${estimate.low.toLocaleString()} – ${estimate.high.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-teal-600">
                      {selectedProperty && <span>{selectedProperty.label}</span>}
                      {selectedService && <><span>·</span><span>{selectedService.label}</span></>}
                    </div>
                  </div>
                )}

                <form
                  data-readdy-form
                  id="calculator-contact-form"
                  onSubmit={handleContactSubmit}
                  className="px-6 py-5 space-y-4"
                >
                  {/* Hidden calculator fields */}
                  <input type="hidden" name="property_type" value={selectedProperty?.label ?? 'Not selected'} />
                  <input type="hidden" name="area_size" value={getAreaLabel(areaSqft)} />
                  <input type="hidden" name="service_type" value={selectedService?.label ?? 'Not selected'} />
                  <input type="hidden" name="severity" value={selectedSeverity?.label ?? 'Not selected'} />
                  <input type="hidden" name="add_ons" value={selectedAddOnItems.map(a => a.label).join(', ') || 'None'} />
                  <input type="hidden" name="estimated_range" value={estimate ? `$${estimate.low.toLocaleString()} – $${estimate.high.toLocaleString()}` : 'Not calculated'} />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formName}
                        onChange={e => setFormName(e.target.value)}
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formPhone}
                        onChange={e => setFormPhone(e.target.value)}
                        placeholder="(209) 000-0000"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formEmail}
                      onChange={e => setFormEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">Additional Details <span className="text-gray-400 font-normal">(Optional)</span></label>
                    <textarea
                      name="message"
                      value={formMessage}
                      onChange={e => {
                        if (e.target.value.length <= 500) setFormMessage(e.target.value);
                      }}
                      placeholder="Describe your situation, address, or any other details..."
                      rows={3}
                      maxLength={500}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-colors resize-none"
                    />
                    <p className="text-right text-xs text-gray-400 mt-1">{formMessage.length}/500</p>
                  </div>

                  {formError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                      <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
                      <p className="text-red-600 text-xs">{formError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowContactModal(false)}
                      className="flex-1 border-2 border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-4 py-2.5 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      {formSubmitting ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-line"></i> Send My Request</>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
